<script setup lang="ts">
import {UseImage} from "@vueuse/components";
import type {CoinPrices} from "@lib/external-data/coinPrices.ts";
import {formatCurrency} from "@lib/formatCurrencyNumbers.ts";
import {converterCurrencies, getSignFromQuote} from "@consts";

const props = defineProps({
  quote: { type: String, required: true },
  crypto: { type: String, required: true },

  quoteName: { type: String, required: true },
  cryptoName: { type: String, required: true },
  exchanges: {
    type: Array as () => CoinPrices,
    required: true
  }
});

const sign = getSignFromQuote(props.quote)


</script>

<template>
  <div class="table-container w-full p-6 border-2 border-brand-stroke/10 rounded-4 rounded-lg text-white overflow-x-auto">
    <table class="w-full text-left">
      <thead>
      <tr class="text-brand-secondary">
        <th class="py-2 px-4 border-b border-brand-stroke/10">{{ $t('exchange') }}</th>
        <th class="py-2 px-4 border-b border-brand-stroke/10">{{ $t('price') }}</th>
        <th class="py-2 px-4 border-b border-brand-stroke/10">{{ $t('volume') }}</th>
        <th class="py-2 px-4 border-b border-brand-stroke/10">{{ $t('quote_volume') }}</th>
        <th class="py-2 px-4 border-b border-brand-stroke/10">{{ $t('percentage') }}</th>
        <th class="py-2 px-4 border-b border-brand-stroke/10">{{ $t('vwap') }}</th>
      </tr>
      </thead>
      <tbody>
      <tr
          v-for="(exchange, index) in exchanges"
          :key="index"
          class="border-b border-brand-stroke/10"
      >
        <td class="py-3 px-4 whitespace-nowrap">
          <div class="flex items-center space-x-2">
            <UseImage
                class="w-10 h-10"
                :src="`/exchanges/icons/${exchange.exchange}.svg`"
                :alt="exchange.exchange + 'Logo'"
            >
              <template #error>
                <div class="bg-white rounded-full w-4 h-4 opacity-20"/>
              </template>
            </UseImage>
            <span>{{ exchange.exchange }}</span>
          </div>
        </td>
        <td class="py-3 px-4 whitespace-nowrap">{{ formatCurrency(exchange.close, sign) }}</td>
        <td class="py-3 px-4 whitespace-nowrap">{{ formatCurrency(exchange.baseVolume, sign) }}</td>
        <td class="py-3 px-4 whitespace-nowrap">{{ formatCurrency(exchange.quoteVolume, sign) }}</td>
        <td class="py-3 px-4 whitespace-nowrap">
            <span
                :class="[
                'rounded-full px-2 py-1 text-xs',
                (exchange.percentage ?? 0) > 0 ? 'bg-brand-primary text-black' : 'bg-red-100 text-black'
              ]"
            >
              {{ exchange.percentage?.toFixed(2) }}%
            </span>
        </td>
        <td class="py-3 px-4 whitespace-nowrap">{{ formatCurrency(exchange.vwap, sign) }}</td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.table-container {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 640px) {
  .table-container {
    max-height: 80vh;
    overflow-y: auto;
  }
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  text-align: left;
  padding: 8px;
}

thead {
  color: #a1a1a1; /* Lighter text color for the table header */
}

tbody tr:hover {
  background-color: #2a2a2a; /* Hover effect for table rows */
}

button {
  transition: background-color 0.3s ease;
}
</style>
